import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Modal, Form, Select, DatePicker, notification, Typography, Space, Tag, Input } from 'antd';
import { PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';
import InvoiceEditorModal from './billing/InvoiceEditorModal';

import { date as fmtDate, label } from '../../lib/format';
export const INVOICE_STATUS_COLORS = {
  DRAFT: 'default',
  APPROVED: 'geekblue',
  ISSUED: 'blue',
  PARTIALLY_PAID: 'orange',
  PAID: 'green',
  CANCELLED: 'red',
};

const money = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Firm-wide invoice register (spec Part VIII). Drafts come from an engagement's billing period or are written by hand. */
export default function InvoicesPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [invoices, setInvoices] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [vatCodes, setVatCodes] = React.useState([]);
  const [engagements, setEngagements] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const linkedClient = new URLSearchParams(window.location.search).get('client_id');
  const [clientFilter, setClientFilter] = React.useState(linkedClient ? Number(linkedClient) : null);
  const [loading, setLoading] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [generateOpen, setGenerateOpen] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [generateForm] = Form.useForm();

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = () => {
    if (!companyId) return;
    setLoading(true);
    const q = { company_id: companyId };
    Promise.all([
      request.get('/invoices', statusFilter ? { ...q, status: statusFilter } : q).then((r) => r.json()),
      request.get('/clients', q).then((r) => r.json()),
      request.get('/services', q).then((r) => r.json()),
      request.get('/vat-setup/vat-codes', q).then((r) => r.json()),
      request.get('/client-service-engagements', q).then((r) => r.json()),
    ])
      .then(([inv, cli, srv, vat, eng]) => {
        if (inv.status) setInvoices(inv.data);
        if (cli.status) setClients(cli.data);
        if (srv.status) setServices(srv.data);
        if (vat.status) setVatCodes(vat.data);
        if (eng.status) setEngagements(eng.data.filter((e) => e.client_id && e.status === 'ACTIVE'));
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchAll, [companyId, statusFilter]);

  const handleGenerate = () => {
    generateForm.validateFields().then((values) => {
      setGenerating(true);
      request
        .post(`/invoices/from-engagement/${values.engagement_id}`, {
          reference_date: values.reference_date ? values.reference_date.format('YYYY-MM-DD') : null,
        })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: `Draft ${r.data.invoice_number} generated for ${r.data.period_label} - total ${money(r.data.total)}` });
            setGenerateOpen(false);
            fetchAll();
          } else {
            notification.error({ message: r.message });
          }
        })
        .finally(() => setGenerating(false));
    });
  };

  const columns = [
    { title: 'Number', dataIndex: 'invoice_number', render: (v, inv) => <Link to={`/invoices/${inv.id}`}>{v}</Link> },
    { title: 'Date', dataIndex: 'invoice_date', sorter: (a, b) => (a.invoice_date < b.invoice_date ? -1 : 1), render: (v) => fmtDate(v) },
    { title: 'Due', dataIndex: 'due_date', render: (v) => fmtDate(v) },
    { title: 'Client', key: 'client', render: (_, inv) => (inv.Client ? <Link to={`/clients/${inv.Client.id}`}>{inv.Client.client_name}</Link> : '-') },
    { title: 'Service / Period', key: 'period', render: (_, inv) => [inv.Engagement?.Service?.service_name, inv.period_label].filter(Boolean).join(' - ') || '-' },
    { title: 'Total', dataIndex: 'total', align: 'right', render: money },
    { title: 'Paid', dataIndex: 'amount_paid', align: 'right', render: money },
    { title: 'Balance', dataIndex: 'balance_due', align: 'right', render: (v) => <strong>{money(v)}</strong> },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={INVOICE_STATUS_COLORS[v]}>{label(v)}</Tag> },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Invoices
        </Typography.Title>
        <Space wrap>
          <Input.Search placeholder="Search number, client, reference" allowClear style={{ width: 240 }} onSearch={setSearch} onChange={(e) => !e.target.value && setSearch('')} />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            placeholder="All clients"
            value={clientFilter}
            onChange={(v) => setClientFilter(v || null)}
            options={clients.map((c) => ({ value: c.id, label: c.client_name }))}
          />
          {isSystemAdmin && (
            <Select style={{ width: 200 }} value={companyId} placeholder="Company" onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />
          )}
          <Select
            allowClear
            style={{ width: 170 }}
            placeholder="All statuses"
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.keys(INVOICE_STATUS_COLORS).map((s) => ({ value: s, label: s.replace('_', ' ') }))}
          />
          <Button
            icon={<ThunderboltOutlined />}
            onClick={() => {
              generateForm.resetFields();
              generateForm.setFieldsValue({ reference_date: moment() });
              setGenerateOpen(true);
            }}
          >
            Generate from Engagement
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditorOpen(true)}>
            New Invoice
          </Button>
        </Space>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={invoices.filter((inv) => (!clientFilter || inv.client_id === clientFilter) && (!search || [inv.invoice_number, inv.Client?.client_name, inv.reference, inv.period_label].some((x) => x && String(x).toLowerCase().includes(search.toLowerCase()))))}
        loading={loading}
        pagination={{ showTotal: (total) => `${total} in total`, showSizeChanger: true }}
        locale={{ emptyText: search || clientFilter ? 'No invoices match the filter' : 'No invoices yet' }}
      />

      <InvoiceEditorModal
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onSaved={() => {
          setEditorOpen(false);
          fetchAll();
        }}
        companyId={companyId}
        clients={clients}
        services={services}
        vatCodes={vatCodes}
        engagements={engagements}
        initial={null}
      />

      <Modal title="Generate Draft from Engagement" open={generateOpen} onCancel={() => setGenerateOpen(false)} onOk={handleGenerate} confirmLoading={generating}>
        <Typography.Paragraph type="secondary">
          Builds the draft for the billing period containing the reference date: a fixed-fee proposal bills its share of the annual value, otherwise the
          client's approved, billable, not-yet-invoiced time for that service is pulled in.
        </Typography.Paragraph>
        <Form form={generateForm} layout="vertical">
          <Form.Item name="engagement_id" label="Service Engagement" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={engagements.map((e) => ({
                value: e.id,
                label: `${e.Client?.client_name || 'Client'} - ${e.Service?.service_name || 'Service'} (${e.frequency})`,
              }))}
            />
          </Form.Item>
          <Form.Item name="reference_date" label="Reference Date (any day in the period to bill)" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
